// Filters 2.0
import { createSelector } from 'reselect';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';

import { itemsFacetMetadataSelector } from './items';

import { APPLIED_COMPONENT_TYPE, visualizationFilters, COMPONENT_TYPE, VISUALIZATION_KEY } from 'Constants/filters';
import { parseAppliedFilters, transformNestedSelect, getAppliedFiltersWithInfo, removeExcludedFilters, getVisualizationFilter } from 'Helpers/filtersTanak';
import { DEFAULT_CATEGORY } from 'Constants/categories';

import { buildFiltersTreeForVariousFilters, getPopularFilters } from 'Helpers/filtersTanakSEO';
import { isEmpty, isObject } from 'Helpers/objects';
import { arrayToObject } from 'Helpers/array';
import { getVisibleCampaignFilters } from './visibleCampaign';
import { isCampaignFilter } from 'Helpers/filters';

export const getFiltersArr = (state, categoryId = DEFAULT_CATEGORY) => {
    const filters = state.filters.elements;

    return (filters[categoryId] && filters[categoryId].filters) || [];
};

export const getVisualizationFilters = createSelector(
    state => state.filters.elements,
    (_, categoryId) => categoryId,
    (filters, categoryId) => {
        const sortVisualization = visualizationFilters.SORT_TYPE;
        const visualization = filters[categoryId]
            && filters[categoryId][VISUALIZATION_KEY]
            && filters[categoryId][VISUALIZATION_KEY][sortVisualization];

        if (!isObject(visualization)) {
            return {};
        }

        return {
            ...getVisualizationFilter(visualization, sortVisualization)
        };
    }
);

export const getCombinedFiltersArr = createSelector(
    getFiltersArr,
    getVisualizationFilters,
    (filters, visualization) => {
        const visualizationAttr = isEmpty(visualization) ? [] : [visualizationFilters.SORT_TYPE];

        return filters ? [
            ...filters,
            ...visualizationAttr
        ] : [];
    }
);

export const getFilterIdToAttrMap = (state, categoryId = DEFAULT_CATEGORY) => {
    const filters = state.filters.elements;

    return filters[categoryId] ? filters[categoryId].filterIdToAttrMap : [];
};

export const getFiltersInfoByAttr = (state, categoryId = DEFAULT_CATEGORY) => {
    const filters = state.filters.elements;

    return filters[categoryId] ? filters[categoryId].filtersInfoByAttr : [];
};

export const getFullFiltersInfo = createSelector(
    getFilterIdToAttrMap,
    getFiltersInfoByAttr,
    getVisibleCampaignFilters,
    (filterIdToAttr, filtersInfo, visibleCampaignFiltersInfo) => {
        const filters = Object.keys(filtersInfo);
        let transformedFilterInfo = {};

        filters.filter(filterAttr => {
            const currFilterVisibleCampaignInfo = visibleCampaignFiltersInfo[filterAttr];

            if ((currFilterVisibleCampaignInfo && currFilterVisibleCampaignInfo.name && currFilterVisibleCampaignInfo.valueName)
                || !currFilterVisibleCampaignInfo) {
                return true;
            }

            return false;
        }).forEach(filterAttr => {
            const currInfo = filtersInfo[filterAttr];

            const currFilterVisibleCampaignInfo = visibleCampaignFiltersInfo[filterAttr];

            if (currFilterVisibleCampaignInfo) {
                currInfo.name = currFilterVisibleCampaignInfo.name;
                currInfo.sectionName = currFilterVisibleCampaignInfo.name;
                currInfo.render.customConfiguration.values[0].name = currFilterVisibleCampaignInfo.valueName;
            }

            if (currInfo.nestedFilterId) {
                transformedFilterInfo = {
                    ...transformedFilterInfo,
                    ...transformNestedSelect(currInfo, filterIdToAttr, filtersInfo)
                };
            }
            else if (!transformedFilterInfo[filterAttr]) {
                transformedFilterInfo[filterAttr] = { ...currInfo };
            }
        });
        return transformedFilterInfo;
    }
);

// todo: remove when abundance experimennt succeeds
export const getCombinedFiltersInfo = createSelector(
    getFullFiltersInfo,
    getVisualizationFilters,
    (filters, visualizationFilters) => ({ ...filters, ...visualizationFilters })
);

export const getCurrentSorting = createSelector(
    itemsFacetMetadataSelector,
    itemsMetadata => {
        const currentSorting = (itemsMetadata && itemsMetadata.applied_sorting) || {};

        return currentSorting.key;
    }
);

/**
 * getAppliedFilter return an object
 * { "price":{id: price, values: {min: 0, max: 1000} }
 * { "brand": {id: brand, values: [{id:'bmw-m6",name:}]} }*/
export const getAppliedFilters = createSelector(
    itemsFacetMetadataSelector,
    itemsMetadata => {
        const appliedFilters = (itemsMetadata && removeExcludedFilters(itemsMetadata.applied_filters)) || [];
        const allFiltersList = (itemsMetadata && removeExcludedFilters(itemsMetadata.filters)) || [];
        const facetsFilters = appliedFilters
            .reduce((acc, { id, description, values, render_as, is_relaxed }) => {
                const tranformedAppliedFilter = {
                    id,
                    description,
                    isRelaxed: is_relaxed,
                    values
                };

                const currentFilter = allFiltersList.find(filter => filter.id === id);

                if (currentFilter?.is_hidden) {
                    tranformedAppliedFilter.is_hidden = true;
                }
                if ([APPLIED_COMPONENT_TYPE.RANGE_INPUT, APPLIED_COMPONENT_TYPE.RANGE_SLIDER].includes(render_as)) {
                    const valuesObj = {};

                    values.forEach(value => {
                        valuesObj[value.id] = value[`${value.id}_value`];
                    });
                    tranformedAppliedFilter.values = [{ ...valuesObj }];
                }
                else {
                    tranformedAppliedFilter.values = [...values];
                }

                return { ...acc, [tranformedAppliedFilter.id]: { ...tranformedAppliedFilter }};
            }, {});

        return facetsFilters;
    }
);

export const getAppliedFiltersLength = createSelector(
    getAppliedFilters,
    appliedFilters => {
        if (appliedFilters) {
            return Object.keys(appliedFilters).length;
        }
        return 0;
    }
);

export const getParsedAppliedFilters = createSelector(
    getAppliedFilters,
    appliedFilters => parseAppliedFilters(appliedFilters)
);

export const getRelaxedFiltersList = createSelector(
    getAppliedFilters,
    appliedFilters => {
        const relaxedFitlers = [];

        if (appliedFilters) {
            for (const filterId in appliedFilters) {
                if (appliedFilters[filterId].isRelaxed) {
                    relaxedFitlers.push(filterId);
                }
            }
        }
        return relaxedFitlers;
    }
);

export const getHiddenFilterIds = createSelector(
    getAppliedFilters,
    appliedFilters => {
        const hiddenFilters = {};

        if (appliedFilters) {
            for (const filterId in appliedFilters) {
                if (appliedFilters[filterId]?.is_hidden) {
                    hiddenFilters[filterId] = appliedFilters[filterId]?.values?.length;
                }
            }
        }
        return hiddenFilters;
    }
);

/**
 * Returns the order in which applied filters need to be displayed
 * @param {Object} state
 */
export const getFiltersRecencyOrder = createSelector(
    state => (state.filters && state.filters.filterRecencyOrder),
    getRelaxedFiltersList,
    (recencyOrder, relaxedFiltersList) => {
        const newFilterRecencyOrder = recencyOrder;

        if (relaxedFiltersList?.length) {
            relaxedFiltersList.forEach(relaxedFilterName => {
                newFilterRecencyOrder.push(newFilterRecencyOrder.splice(newFilterRecencyOrder.indexOf(relaxedFilterName), 1)[0]);
            });
        }
        return newFilterRecencyOrder;
    }
);
/**
 * To computue and return filters with final Filter Pane Id
 * @param {Object} state
 * @param {String | Number} categoryId
 * @returns {Object}
 */
export const computeFilterPaneIds = createSelector(
    getFullFiltersInfo,
    filtersMap => {
        const keys = Object.keys(filtersMap);
        const response = {};

        keys.forEach(key => {
            let currentFilter = filtersMap[key];
            const identifier = currentFilter.id;

            while (currentFilter.parentFilterAttr) {
                currentFilter = filtersMap[currentFilter.parentFilterAttr];
            }

            response[identifier] = {};
            response[identifier].filterPaneId = currentFilter.attribute;
        });

        return response;
    }
);

export const getAppliedFiltersLabels = createSelector(
    getParsedAppliedFilters,
    getFullFiltersInfo,
    getFiltersRecencyOrder,
    getAppliedFilters,
    (appliedFilters, filtersInfoByAttr, filterOrder, appliedFiltersInfo) =>
        getAppliedFiltersWithInfo(appliedFilters, filtersInfoByAttr, filterOrder, appliedFiltersInfo)
);

// To get Quick Filters from the state
export const getQuickFilters = (state, categoryId) => state.filters.elements[categoryId] && state.filters.elements[categoryId].quickFilters;

/**
 * To get Applied Filters chips
 */
export const getTags = createSelector(
    getParsedAppliedFilters,
    getFullFiltersInfo,
    getFiltersRecencyOrder,
    computeFilterPaneIds,
    getAppliedFilters,
    (parsedAppliedFilters, filtersMap, filterOrder, filtersPaneInfo, appliedFiltersInfo) => {
        const appliedFiltersKeys = Object.keys(parsedAppliedFilters).filter(key => {
            return filtersMap[key] && filtersMap[key].isTagVisible && parsedAppliedFilters[key];
        });
        let order = appliedFiltersKeys;

        if (filterOrder && filterOrder.length) {
            order = filterOrder.filter(key => appliedFiltersKeys.includes(key));
        }

        const tags = getAppliedFiltersWithInfo(parsedAppliedFilters, filtersMap, order, appliedFiltersInfo);

        return tags.map(filter => {
            filter.filterPaneId = filtersPaneInfo[filter.id].filterPaneId;
            return filter;
        });
    }
);

/**
 * To get visible Quick Filters i.e. show only those Filters that can have multiple values i.e. Brand, Model
 */
export const getQuickFiltersVisible = createSelector(
    getTags,
    getQuickFilters,
    computeFilterPaneIds,
    (appliedFilters, quickFilters, filtersPaneInfo) => {
        const haveQuickFilters = quickFilters && quickFilters.length;

        if (haveQuickFilters) {
            quickFilters.forEach(filter => {
                filter.filterPaneId = filtersPaneInfo[filter.filterId].filterPaneId;
            });
        }
        else {
            return [];
        }

        if (!appliedFilters.length) {
            return quickFilters;
        }

        const multipleAppliedFilters = groupBy(appliedFilters, 'isMultiple');

        if (multipleAppliedFilters.false) {
            const ids = new Set(map(multipleAppliedFilters.false, 'id'));

            quickFilters = quickFilters.filter(filter => !ids.has(filter.filterId));
        }

        return quickFilters;
    }
);
/**
 * Get filters in tree form, so it can be shown for filters seo page
 */
export const getFiltersTreeForSEO = createSelector(
    getFiltersArr,
    getFullFiltersInfo,
    (filters, filtersInfoByAttr) => {
        const filtersTreeViewList = filters
            .reduce((acc, filter) => {
                if (filtersInfoByAttr[filter]) {
                    const filterInfo = filtersInfoByAttr[filter];
                    const { render: { defaultComponent: { componentType } = {}} = {}} = filterInfo;

                    // show filters that have mapping in buildFiltersTreeForVariousFilters and not nested
                    if (buildFiltersTreeForVariousFilters[componentType] && filterInfo.sectionName) {
                        const filterObj = buildFiltersTreeForVariousFilters[componentType](filterInfo, filtersInfoByAttr);

                        if (filterObj) {
                            acc.push(filterObj);
                        }
                    }
                }
                return acc;
            }, []);

        return filtersTreeViewList;
    }
);

/**
 * Get filters popular filters to show in footer
 */
export const getPopularFiltersForFooter = createSelector(
    getFiltersArr,
    getFullFiltersInfo,
    (filters, filtersInfoByAttr) => {
        const popularFilters = filters
            .filter(filter => !isCampaignFilter(filter))
            .reduce((acc, filter) => {
                if (filtersInfoByAttr[filter]) {
                    const filterInfo = filtersInfoByAttr[filter];
                    const { render: { defaultComponent: { componentType } = {}} = {}} = filterInfo;

                    // show filters that have mapping in buildFiltersTreeForVariousFilters
                    if (getPopularFilters[componentType] && filterInfo.sectionName) {
                        const filterObj = getPopularFilters[componentType](filterInfo);

                        if (filterObj) {
                            acc.push(filterObj);
                        }
                    }
                }
                return acc;
            }, []);

        return popularFilters;
    }
);

/**
 * Get nested filters for footer
 */
export const getNestedFiltersWithChildren = createSelector(
    getFiltersArr,
    getFullFiltersInfo,
    (filters, filtersInfoByAttr) => {
        const { NESTED_SELECT } = COMPONENT_TYPE;
        const nestedFiltersWithChildren = filters
            .reduce((acc, filter) => {
                if (filtersInfoByAttr[filter]) {
                    const filterInfo = filtersInfoByAttr[filter];
                    const { render: { defaultComponent: { componentType } = {}} = {}} = filterInfo;

                    // show filters that have mapping in buildFiltersTreeForVariousFilters
                    if (buildFiltersTreeForVariousFilters[componentType] && filterInfo.sectionName && (componentType === NESTED_SELECT)) {
                        const filterObj = buildFiltersTreeForVariousFilters[componentType](filterInfo, filtersInfoByAttr);

                        if (filterObj) {
                            // convert array to map
                            const { values } = filterObj;
                            const valuesMap = {};

                            values.forEach(value => {
                                valuesMap[value.id] = value;
                            });

                            acc.push({
                                ...filterObj,
                                valuesMap
                            });
                        }
                    }
                }
                return acc;
            }, []);

        return nestedFiltersWithChildren;
    }
);

export const getQuickFiltersByName = createSelector(
    getQuickFilters,
    quickFilters => map(quickFilters, 'name').join(',')
);

export const getSortingOptions = (state, categoryId) => {
    const filters = state.filters.elements;
    const visualization = filters[categoryId] && filters[categoryId][VISUALIZATION_KEY];

    return visualization ? visualization[visualizationFilters.SORT_TYPE].options : [];
};

export const getSitemapFilters = createSelector(
    state => state?.filtersAbundance?.data?.filters,
    (_, __, config) => config,
    getFiltersArr,
    getFullFiltersInfo,
    (abundanceFilters, config, filters, filtersInfoByAttr) => {
        const abundanceFilterObj = arrayToObject(abundanceFilters, 'id');
        const { make, model } = config.get('adpvAuto', 'keys');
        const filtersTreeViewList = filters
            .reduce((acc, filter) => {
                if (filtersInfoByAttr[filter] && (filter === make || filter === model)) {
                    const filterInfo = filtersInfoByAttr[filter];
                    const { render: { defaultComponent: { componentType } = {}} = {}} = filterInfo;

                    // show filters that have mapping in buildFiltersTreeForVariousFilters and not nested
                    if (buildFiltersTreeForVariousFilters[componentType] && filterInfo.sectionName) {
                        const filterObj = buildFiltersTreeForVariousFilters[componentType](filterInfo, filtersInfoByAttr, abundanceFilterObj);

                        if (filterObj) {
                            acc.push(filterObj);
                        }
                    }
                }
                return acc;
            }, []);

        return filtersTreeViewList;
    }
);
